if (!customElements.get('product-info')) {
  function hexToRGBA(hex, alpha) {
    hex = hex.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

        this.setAttribute('style', `${this.getAttribute('style')} --pdp-second-theme-color : ${hexToRGBA(this.getAttribute('data-theme-color'), 0.5)};`);
        this.initQuantityHandlers();
        this.initSellingPlanHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode) : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(document.querySelector('main'), html.querySelector('main'), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(this, html.querySelector('product-info'), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true, FoxTheme.variantStrings.soldOut);

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      initSellingPlanHandlers() {
        const ageSelectors = this.querySelectorAll('.product-selling-plan__age-selector');
        const sellingPlanInput = this.querySelector('.product-selling-plan__input-sale');
        const subscribeInput = this.querySelector('input[name="selling_plan"]');
        const oneTimeInput = this.querySelector('input[name="selling_plan"][value=""]');

        // Update sticky button price initially
        this.updateStickyButtonPrice();

        if (!ageSelectors.length || !sellingPlanInput) return;

        ageSelectors.forEach(function (radio) {
          radio.addEventListener('change', function () {
            if (this.checked) {
              sellingPlanInput.value = this.value;
              sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
              // Update sticky button price when age selector changes
              this.closest('product-info').updateStickyButtonPrice();
              // Update sticky selling plan name when age selector changes
              this.closest('product-info').updateStickySellingPlanName();
              // Update sticky age group item when age selector changes
              this.closest('product-info').updateStickyAgeGroupItem();

              // Update the properties[age_group] field with the selected age group name
              const ageGroupProperty = this.closest('product-info').querySelector('input[name="properties[age_group]"]');
              const ageGroupPropertyForm = this.closest('product-info').querySelector('.product-age-group-property');
              if (ageGroupProperty || ageGroupPropertyForm) {
                // Get the age group name from the label
                const ageGroupLabel = this.closest('.product-selling-plan__age-selectors').querySelector('.age-selector__title');
                if (ageGroupLabel) {
                  const ageGroupValue = ageGroupLabel.textContent.trim();
                  if (ageGroupProperty) {
                    ageGroupProperty.value = ageGroupValue;
                  }
                  if (ageGroupPropertyForm) {
                    ageGroupPropertyForm.value = ageGroupValue;
                  }
                }
              }

              // Update sticky button price when age selector changes

              this.closest('product-info').updateStickyButtonPrice();
            }
          });
        });

        // Helper to update bundle-and-save cards when selling plan mode changes
        const updateBundleCards = (mode) => {
          const bundleBlock = this.querySelector('.bundle-and-save');
          if (!bundleBlock) return;

          if (mode === 'subscribe') {
            bundleBlock.classList.add('bundle-and-save--subscribe-selected');
          } else {
            bundleBlock.classList.remove('bundle-and-save--subscribe-selected');
          }
        };

        // Handle subscribe option selection
        if (subscribeInput) {
          subscribeInput.addEventListener('change', function () {
            if (this.checked) {
              const checkedAgeSelector = this.closest('product-info').querySelector('.product-selling-plan__age-selector:checked');
              if (checkedAgeSelector) {
                this.value = checkedAgeSelector.value;

                const formInput = this.closest('product-info').querySelector('.product-selling-plan-form-input');
                if (formInput) {
                  formInput.value = checkedAgeSelector.value;
                }

                // Update the properties[age_group] field when subscribe is selected
                const ageGroupProperty = this.closest('product-info').querySelector('input[name="properties[age_group]"]');
                const ageGroupPropertyForm = this.closest('product-info').querySelector('.product-age-group-property');
                if (ageGroupProperty || ageGroupPropertyForm) {
                  const ageGroupLabel = checkedAgeSelector.closest('.product-selling-plan__age-selectors').querySelector('.age-selector__title');
                  if (ageGroupLabel) {
                    const ageGroupValue = ageGroupLabel.textContent.trim();
                    if (ageGroupProperty) {
                      ageGroupProperty.value = ageGroupValue;
                    }
                    if (ageGroupPropertyForm) {
                      ageGroupPropertyForm.value = ageGroupValue;
                    }
                  }
                }
              }

              const detailsElement = this.closest('.product-selling-plans__subscribe-details');
              if (detailsElement) {
                detailsElement.setAttribute('open', '');
              }
            } else {
              this.value = '';

              const formInput = this.closest('product-info').querySelector('.product-selling-plan-form-input');
              if (formInput) {
                formInput.value = '';
              }

              // Clear the properties[selling_plan_id] field when subscribe is deselected
              const sellingPlanIdProperty = this.closest('product-info').querySelector('input[name="properties[selling_plan_id]"]');
              if (sellingPlanIdProperty) {
                sellingPlanIdProperty.value = '';
              }

              // Close the accordion when subscribe is deselected
              const detailsElement = this.closest('.product-selling-plans__subscribe-details');
              if (detailsElement) {
                detailsElement.removeAttribute('open');
              }
            }
            // Update sticky button price when subscribe option changes
            this.closest('product-info').updateStickyButtonPrice();
            // Update sticky selling plan name when subscribe option changes
            this.closest('product-info').updateStickySellingPlanName();
            // Update sticky age group item when subscribe option changes
            this.closest('product-info').updateStickyAgeGroupItem();
            // Update bundle cards mode
            updateBundleCards(this.checked ? 'subscribe' : 'onetime');
          });
        }

        // Handle one-time purchase selection
        if (oneTimeInput) {
          oneTimeInput.addEventListener('change', function () {
            if (this.checked) {
              // Clear any selling plan value when one-time is selected
              const subscribeInput = this.closest('product-info').querySelector('input[name="selling_plan"]:not([value=""])');
              if (subscribeInput) {
                subscribeInput.value = '';
              }

              const formInput = this.closest('product-info').querySelector('.product-selling-plan-form-input');
              if (formInput) {
                formInput.value = '';
              }

              // Clear the properties[selling_plan_id] field when one-time is selected
              const sellingPlanIdProperty = this.closest('product-info').querySelector('input[name="properties[selling_plan_id]"]');
              if (sellingPlanIdProperty) {
                sellingPlanIdProperty.value = '';
              }

              const detailsElement = this.closest('product-info').querySelector('.product-selling-plans__subscribe-details');
              if (detailsElement) {
                detailsElement.removeAttribute('open');
              }
            }
            // Update sticky button price when one-time option changes
            this.closest('product-info').updateStickyButtonPrice();
            // Update sticky selling plan name when one-time option changes
            this.closest('product-info').updateStickySellingPlanName();
            // Update sticky age group item when one-time option changes
            this.closest('product-info').updateStickyAgeGroupItem();
            // Update bundle cards mode
            updateBundleCards(this.checked ? 'onetime' : 'subscribe');
          });
        }

        const checkedAgeSelector = this.querySelector('.product-selling-plan__age-selector:checked');
        if (checkedAgeSelector) {
          sellingPlanInput.value = checkedAgeSelector.value;

          // Set the initial properties[selling_plan_id] field value
          const sellingPlanIdProperty = this.querySelector('input[name="properties[selling_plan_id]"]');
          if (sellingPlanIdProperty) {
            sellingPlanIdProperty.value = checkedAgeSelector.value;
          }

          // Set the initial properties[age_group] field value
          const ageGroupProperty = this.querySelector('input[name="properties[age_group]"]');
          const ageGroupPropertyForm = this.querySelector('.product-age-group-property');
          if (ageGroupProperty || ageGroupPropertyForm) {
            const ageGroupLabel = checkedAgeSelector.closest('.product-selling-plan__age-selectors').querySelector('.age-selector__title');
            if (ageGroupLabel) {
              const ageGroupValue = ageGroupLabel.textContent.trim();
              if (ageGroupProperty) {
                ageGroupProperty.value = ageGroupValue;
              }
              if (ageGroupPropertyForm) {
                ageGroupPropertyForm.value = ageGroupValue;
              }
            }
          }
        }

        // Set subscribe option as checked by default if there's a checked age selector
        if (checkedAgeSelector && subscribeInput) {
          subscribeInput.checked = true;
          subscribeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Handle serving guide click
        this.initServingGuideClick();

        // Update sticky selling plan name initially
        this.updateStickySellingPlanName();

        // Update sticky age group item initially
        this.updateStickyAgeGroupItem();
      }

      updateStickyButtonPrice() {
        // select the sticky button price element globally
        const sticktyBtnPrice = document.querySelector('.sticky-atc-bar__price');
        if (!sticktyBtnPrice) {
          console.log('Sticky button price element not found');
          return;
        }

        const selectedSellingPlan = this.querySelector('input[name="selling_plan"]:checked');

        if (selectedSellingPlan) {
          const selectedLabel = selectedSellingPlan.closest('.product-selling-plans__conent');
          const priceElement = selectedLabel?.querySelector('.product-selling-plans__subscribe-price');

          if (priceElement) {
            sticktyBtnPrice.textContent = priceElement.textContent;
          } else {
            console.log('Price element not found in selected label');
          }
        } else {
          console.log('No selling plan option selected');
        }

        // Update selling plan name in sticky ATC bar
        this.updateStickySellingPlanName();
      }

      updateStickySellingPlanName() {
        // Get the sticky ATC bar selling plan name element
        const stickySellingPlanName = document.querySelector('.sticky-atc-bar__selling-plan-name-item');
        if (!stickySellingPlanName) {
          console.log('Sticky selling plan name element not found');
          return;
        }

        try {
          // Check if subscribe option is selected
          const subscribeInput = this.querySelector('input[name="selling_plan"]:checked');
          const oneTimeInput = this.querySelector('input[name="selling_plan"][value=""]:checked');

          if (subscribeInput && subscribeInput.value !== '') {
            // Subscribe option is selected, get the selling plan name from the checked age selector
            const checkedAgeSelector = this.querySelector('.product-selling-plan__age-selector:checked');

            if (checkedAgeSelector) {
              const sellingPlanName = checkedAgeSelector.getAttribute('data-selling-plan-name');
              if (sellingPlanName) {
                // Extract the part before "subscription" if it exists
                const displayName = sellingPlanName.split('subscription')[0].trim();
                stickySellingPlanName.textContent = 'Delivery every ' + displayName;
              } else {
                stickySellingPlanName.textContent = 'Subscription';
              }
            } else {
              stickySellingPlanName.textContent = 'Subscription';
            }
          } else if (oneTimeInput && oneTimeInput.checked) {
            // One-time purchase is selected
            stickySellingPlanName.textContent = 'One Time Purchase';
          } else {
            // No option selected, default to one-time purchase
            stickySellingPlanName.textContent = 'One Time Purchase';
          }
        } catch (error) {
          console.error('Error updating sticky selling plan name:', error);
          stickySellingPlanName.textContent = 'One Time Purchase';
        }

        // Update age group item in sticky ATC bar
        this.updateStickyAgeGroupItem();
      }

      updateStickyAgeGroupItem() {
        // Get the sticky ATC bar age group item element
        const stickyAgeGroupItem = document.querySelector('.sticky-atc-bar__age-group-item');
        if (!stickyAgeGroupItem) {
          console.log('Sticky age group item element not found');
          return;
        }

        try {
          // Get the checked age selector regardless of selling plan selection
          const checkedAgeSelector = this.querySelector('.product-selling-plan__age-selector:checked');

          if (checkedAgeSelector) {
            // Find the age selector title container within the same label
            const ageSelectorTitleContainer = checkedAgeSelector.closest('label').querySelector('.age-selector__title');
            if (ageSelectorTitleContainer) {
              const ageSelectorName = ageSelectorTitleContainer.getAttribute('data-age-selector-name');
              if (ageSelectorName) {
                stickyAgeGroupItem.textContent = ageSelectorName;
              } else {
                stickyAgeGroupItem.textContent = '';
              }
            } else {
              stickyAgeGroupItem.textContent = '';
            }
          } else {
            // No age selector checked, clear age group
            stickyAgeGroupItem.textContent = '';
          }
        } catch (error) {
          console.error('Error updating sticky age group item:', error);
          stickyAgeGroupItem.textContent = '';
        }
      }

      async initServingGuideClick() {
        const servingGuideLabels = this.querySelectorAll('.product-selling-plans__age-selectors-guid-seving-label');

        if (!servingGuideLabels.length) return;

        servingGuideLabels.forEach((servingGuideLabel) => {
          servingGuideLabel.addEventListener('click', async () => {
            const allTabs = this.querySelectorAll('details[is="accordion-group"]');

            for (const tab of allTabs) {
              const heading = tab.querySelector('summary h2');
              if (heading && heading.textContent.toLowerCase().includes('how to take')) {
                // Check if tab is currently closed
                const wasClosed = !tab.hasAttribute('open');

                if (wasClosed) {
                  const summary = tab.querySelector('summary');
                  if (summary) {
                    summary.click();
                  }
                }
                break;
              }
            }
          });

          servingGuideLabel.style.cursor = 'pointer';
        });
      }
    }
  );
}
